import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import {
  OPPORTUNITY_STATUSES, OPPORTUNITY_PHASES, BUILDER_STATUSES,
  INDUSTRY_OPTIONS, PROJECT_TYPE_OPTIONS, QUOTE_STATUSES,
  calculateQuoteTotals
} from '../data/mockData';
import {
  Plus,
  Search,
  MoreHorizontal,
  Building2,
  Users,
  DollarSign,
  Phone,
  Mail,
  MessageSquare,
  FileText,
  Trophy,
  Briefcase,
  StickyNote,
  Trash2,
  ChevronDown,
  ChevronRight,
  Tag,
  Hash,
  X,
  Pencil,
  Calendar,
  ExternalLink,
  FileText as FileTextIcon,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

function getPrimaryContact(builder) {
  if (!builder) return null;
  if (Array.isArray(builder.contacts) && builder.contacts.length > 0) {
    return builder.contacts[0];
  }
  if (builder.contact || builder.email || builder.phone) {
    return { name: builder.contact || '', email: builder.email || '', phone: builder.phone || '', role: '' };
  }
  return null;
}

// ─── Main Module ─────────────────────────────────────────────────────────────

export function CRMModule() {
  const { state, actions } = useApp();
  const { opportunities, currentOpportunity, currentBuilder, quotes } = state;
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewOppDialog, setShowNewOppDialog] = useState(false);
  const [editingOpp, setEditingOpp] = useState(false);
  const [showNewBuilderDialog, setShowNewBuilderDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showWinConfirm, setShowWinConfirm] = useState(false);

  const normalizedQuery = searchQuery.toLowerCase();
  const filteredOpps = opportunities.filter(opp =>
    opp.name.toLowerCase().includes(normalizedQuery) ||
    opp.developer.toLowerCase().includes(normalizedQuery) ||
    (opp.industry || '').toLowerCase().includes(normalizedQuery) ||
    (opp.projectDescription || '').toLowerCase().includes(normalizedQuery) ||
    (opp.tenderLinkId || '').toLowerCase().includes(normalizedQuery) ||
    (opp.projectTypes || []).some(type => type.toLowerCase().includes(normalizedQuery)) ||
    (opp.jobChaseNotes || '').toLowerCase().includes(normalizedQuery) ||
    (opp.phase || '').toLowerCase().includes(normalizedQuery)
  );

  const handleSelectOpp = (id) => actions.setCurrentOpportunity(id);
  const handleSelectBuilder = (id) => actions.setCurrentBuilder(id);

  const handleCreateQuote = (builderId) => {
    if (!currentOpportunity) return;
    actions.createQuote(currentOpportunity.id, builderId);
    toast.success('Quote created — Opening Door Scheduler');
  };

  const handleOpenQuote = (quoteId) => {
    actions.setCurrentQuote(quoteId);
    actions.setActiveModule('scheduling');
  };

  const handleMarkWon = () => {
    if (!currentOpportunity || !currentBuilder) return;
    actions.markBuilderWon(currentOpportunity.id, currentBuilder.id);
    toast.success(`${currentBuilder.name} marked as winner! Quote is now Active Schedule.`);
    setShowWinConfirm(false);
  };

  return (
    <div className="h-full flex" data-testid="crm-module">

      {/* ── Left: Opportunities Sidebar ── */}
      <div className="w-72 border-r border-border flex flex-col bg-white shrink-0">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-base">Opportunities</h2>
            <Button size="sm" onClick={() => setShowNewOppDialog(true)} data-testid="new-opp-btn">
              <Plus className="w-4 h-4 mr-1" />
              New
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-8 text-sm"
              data-testid="opp-search"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {filteredOpps.length === 0 ? (
              <EmptyOppState onLoadSample={actions.loadSampleData} />
            ) : (
              filteredOpps.map(opp => (
                <OpportunityCard
                  key={opp.id}
                  opportunity={opp}
                  selected={currentOpportunity?.id === opp.id}
                  onClick={() => handleSelectOpp(opp.id)}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* ── Right: Opportunity Detail ── */}
      {currentOpportunity ? (
        <div className="flex-1 flex flex-col overflow-hidden bg-muted/20">

          {/* Compact Opportunity Header */}
          <OpportunityHeader
            opportunity={currentOpportunity}
            onAddBuilder={() => setShowNewBuilderDialog(true)}
            onEdit={() => setEditingOpp(true)}
            editing={editingOpp}
            onCancelEdit={() => setEditingOpp(false)}
          />

          {/* Quotes / Schedules panel */}
          <OpportunityQuotesSection
            quotes={quotes}
            opportunity={currentOpportunity}
            onOpenQuote={handleOpenQuote}
          />

          {/* Builders + Builder Detail — or inline edit form */}
          {editingOpp ? (
            <InlineEditOppForm
              opportunity={currentOpportunity}
              onSave={(data) => {
                actions.updateOpportunity(currentOpportunity.id, data);
                setEditingOpp(false);
                toast.success('Opportunity updated');
              }}
              onCancel={() => setEditingOpp(false)}
            />
          ) : (
          <div className="flex-1 flex overflow-hidden">

            {/* Builder List */}
            <div className="w-64 shrink-0 border-r border-border bg-white flex flex-col">
              <div className="px-4 py-2 border-b border-border flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Builders ({currentOpportunity.builders.length})
                </span>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {currentOpportunity.builders.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-7 h-7 mx-auto mb-2 opacity-40" />
                      <p className="text-xs">No builders yet</p>
                    </div>
                  ) : (
                    currentOpportunity.builders.map(builder => (
                      <BuilderRow
                        key={builder.id}
                        builder={builder}
                        selected={currentBuilder?.id === builder.id}
                        onClick={() => handleSelectBuilder(builder.id)}
                        onCreateQuote={() => handleCreateQuote(builder.id)}
                        onOpenQuote={() => handleOpenQuote(builder.quoteId)}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Builder Detail */}
            <div className="flex-1 overflow-auto">
              {currentBuilder ? (
                <BuilderDetail
                  builder={currentBuilder}
                  onAddActivity={() => setShowActivityDialog(true)}
                  onMarkWon={() => setShowWinConfirm(true)}
                  onUpdateBuilder={(updates) => actions.updateBuilder(currentOpportunity.id, currentBuilder.id, updates)}
                  onUpdateStatus={(status) => {
                    actions.updateBuilder(currentOpportunity.id, currentBuilder.id, { status });
                    toast.success(`Status updated to ${status}`);
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Select a builder to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-muted/20">
          <div className="text-center">
            <Briefcase className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-muted-foreground text-sm">Select an opportunity to get started</p>
          </div>
        </div>
      )}

      {/* ── Dialogs ── */}
      <NewOpportunityDialog
        open={showNewOppDialog}
        onClose={() => setShowNewOppDialog(false)}
        onCreate={(data) => {
          actions.createOpportunity(data);
          setShowNewOppDialog(false);
          toast.success('Opportunity created');
        }}
      />

      <NewBuilderDialog
        open={showNewBuilderDialog}
        onClose={() => setShowNewBuilderDialog(false)}
        onCreate={(data) => {
          if (!currentOpportunity) return;
          actions.addBuilder(currentOpportunity.id, data);
          setShowNewBuilderDialog(false);
          toast.success('Builder added');
        }}
      />

      <AddActivityDialog
        open={showActivityDialog}
        onClose={() => setShowActivityDialog(false)}
        onAdd={(activity) => {
          if (!currentOpportunity || !currentBuilder) return;
          actions.addActivity(currentOpportunity.id, currentBuilder.id, activity);
          setShowActivityDialog(false);
          toast.success('Activity added');
        }}
      />

      <AlertDialog open={showWinConfirm} onOpenChange={setShowWinConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Builder as Winner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark {currentBuilder?.name} as the winner. All other builders will be marked as lost, and the quote will become an Active Schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkWon} data-testid="confirm-win-btn">
              Confirm Win
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Opportunity Header ───────────────────────────────────────────────────────

// ─── Quotes / Schedules Section ──────────────────────────────────────────────

const QUOTE_STATUS_STYLES = {
  draft:     'bg-slate-100 text-slate-700',
  submitted: 'bg-blue-100 text-blue-700',
  active:    'bg-green-100 text-green-700',
};

function OpportunityQuotesSection({ quotes, opportunity, onOpenQuote }) {
  const oppQuotes = quotes.filter(q => q.opportunityId === opportunity.id);
  if (oppQuotes.length === 0) return null;

  return (
    <div className="bg-white border-b border-border px-5 py-2 shrink-0">
      <div className="flex items-center gap-2 mb-1.5">
        <FileTextIcon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Quotes &amp; Schedules ({oppQuotes.length})
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {oppQuotes.map(q => {
          const builder = opportunity.builders.find(b => b.id === q.builderId);
          const totals = calculateQuoteTotals(q);
          const statusLabel = QUOTE_STATUSES.find(s => s.id === q.status)?.name || q.status;
          return (
            <button
              key={q.id}
              onClick={() => onOpenQuote(q.id)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted hover:border-primary/40 transition-colors text-left"
            >
              <span className="text-sm font-medium truncate max-w-[160px]">
                {builder?.name || 'Unknown Builder'}
              </span>
              {q.version > 1 && (
                <span className="text-xs text-muted-foreground">v{q.version}</span>
              )}
              <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full', QUOTE_STATUS_STYLES[q.status] || 'bg-gray-100 text-gray-700')}>
                {statusLabel}
              </span>
              {totals.doorCount > 0 && (
                <span className="text-xs text-muted-foreground">{totals.doorCount}D</span>
              )}
              <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Opportunity Header ───────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

function OpportunityHeader({ opportunity, onAddBuilder, onEdit, editing, onCancelEdit }) {
  const [expanded, setExpanded] = useState(false);
  const phase = OPPORTUNITY_PHASES.find(p => p.id === opportunity.phase);
  const hasDetails = !!(
    opportunity.projectDescription || opportunity.notes ||
    opportunity.jobChaseNotes || opportunity.jobChaseDate ||
    opportunity.expectedProjectStart || opportunity.expectedProjectFinish ||
    opportunity.expectedSupplyStart
  );

  return (
    <div className="bg-white border-b border-border px-5 py-3 shrink-0">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <h1 className="text-lg font-semibold truncate">{opportunity.name}</h1>
          <StatusBadge status={opportunity.status} type="opportunity" small />
          {phase && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 uppercase tracking-wide">
              {phase.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {editing ? (
            <Button variant="ghost" size="sm" onClick={onCancelEdit} title="Cancel editing" className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4 mr-1" />
              Cancel Edit
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={onEdit} data-testid="edit-opp-btn" title="Edit opportunity">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={onAddBuilder} data-testid="add-builder-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Builder
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Metadata chip row — only populated fields */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
        {opportunity.developer && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Building2 className="w-3 h-3" />
            {opportunity.developer}
          </span>
        )}
        {opportunity.industry && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Briefcase className="w-3 h-3" />
            {opportunity.industry}
          </span>
        )}
        {opportunity.estimatedValue > 0 && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <DollarSign className="w-3 h-3" />
            {(opportunity.estimatedValue / 1000).toFixed(0)}k
          </span>
        )}
        {opportunity.tenderLinkId && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Hash className="w-3 h-3" />
            {opportunity.tenderLinkId}
          </span>
        )}
        {opportunity.jobChaseDate && (
          <span className="flex items-center gap-1 text-xs text-amber-700" title="Job Chase Date">
            <Calendar className="w-3 h-3" />
            Chase: {fmtDate(opportunity.jobChaseDate)}
          </span>
        )}
        {opportunity.expectedSupplyStart && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground" title="Expected Supply Start">
            <Calendar className="w-3 h-3" />
            Supply: {fmtDate(opportunity.expectedSupplyStart)}
          </span>
        )}
        {opportunity.expectedProjectStart && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground" title="Expected Project Start">
            <Calendar className="w-3 h-3" />
            Start: {fmtDate(opportunity.expectedProjectStart)}
          </span>
        )}
        {opportunity.expectedProjectFinish && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground" title="Expected Project Finish">
            <Calendar className="w-3 h-3" />
            Finish: {fmtDate(opportunity.expectedProjectFinish)}
          </span>
        )}
        {(opportunity.projectTypes || []).map(type => (
          <Badge key={type} variant="outline" className="text-[10px] h-4 px-1.5 font-normal capitalize">
            {type}
          </Badge>
        ))}
        {hasDetails && (
          <button
            className="flex items-center gap-0.5 text-xs text-primary hover:text-primary/80 ml-auto"
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {expanded ? 'Less' : 'Details'}
          </button>
        )}
      </div>

      {/* Collapsible description / notes / job chase */}
      {expanded && hasDetails && (
        <div className="mt-2 pt-2 border-t border-border space-y-1.5">
          {opportunity.projectDescription && (
            <p className="text-xs text-muted-foreground leading-relaxed">{opportunity.projectDescription}</p>
          )}
          {opportunity.notes && (
            <p className="text-xs text-muted-foreground leading-relaxed italic">{opportunity.notes}</p>
          )}
          {opportunity.jobChaseNotes && (
            <p className="text-xs text-amber-700 leading-relaxed">
              <span className="font-medium">Job Chase: </span>{opportunity.jobChaseNotes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Opportunity Card (sidebar) ───────────────────────────────────────────────

function EmptyOppState({ onLoadSample }) {
  return (
    <div className="text-center py-8 px-2">
      <Briefcase className="w-9 h-9 mx-auto mb-2 text-muted-foreground/40" />
      <p className="text-xs text-muted-foreground mb-3">No opportunities yet</p>
      <Button variant="outline" size="sm" onClick={onLoadSample} data-testid="load-sample-crm-btn">
        Load Sample Data
      </Button>
    </div>
  );
}

function OpportunityCard({ opportunity, selected, onClick }) {
  const wonBuilder = opportunity.builders.find(b => b.status === 'won');
  return (
    <div
      className={cn(
        "px-3 py-2.5 rounded-lg border cursor-pointer transition-all",
        selected
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/40 bg-white"
      )}
      onClick={onClick}
      data-testid={`opp-card-${opportunity.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate leading-tight">{opportunity.name}</p>
          {opportunity.developer && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{opportunity.developer}</p>
          )}
        </div>
        <StatusBadge status={opportunity.status} type="opportunity" small />
      </div>
      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          {opportunity.builders.length}
        </span>
        {opportunity.estimatedValue > 0 && (
          <span className="flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            {(opportunity.estimatedValue / 1000).toFixed(0)}k
          </span>
        )}
        {wonBuilder && (
          <span className="flex items-center gap-1 text-green-600">
            <Trophy className="w-3 h-3" />
            Won
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Builder Row (compact list) ───────────────────────────────────────────────

function BuilderRow({ builder, selected, onClick, onCreateQuote, onOpenQuote }) {
  const primaryContact = getPrimaryContact(builder);
  return (
    <div
      className={cn(
        "px-3 py-2 rounded-lg border cursor-pointer transition-all",
        selected
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/40 bg-white"
      )}
      onClick={onClick}
      data-testid={`builder-card-${builder.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate leading-tight">{builder.name}</p>
          {primaryContact?.name && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{primaryContact.name}</p>
          )}
        </div>
        <StatusBadge status={builder.status} type="builder" small />
      </div>
      <div className="mt-2">
        {builder.quoteId ? (
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-xs w-full"
            onClick={(e) => { e.stopPropagation(); onOpenQuote(); }}
            data-testid={`open-quote-${builder.id}`}
          >
            Open Quote
          </Button>
        ) : (
          <Button
            size="sm"
            className="h-6 text-xs w-full"
            onClick={(e) => { e.stopPropagation(); onCreateQuote(); }}
            data-testid={`create-quote-${builder.id}`}
          >
            Create Quote
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Builder Detail (tabbed) ──────────────────────────────────────────────────

function BuilderDetail({ builder, onAddActivity, onMarkWon, onUpdateStatus, onUpdateBuilder }) {
  const [tab, setTab] = useState('contacts');
  const contacts = Array.isArray(builder.contacts) ? builder.contacts : [];
  const primaryContact = getPrimaryContact(builder);

  const syncLegacyFields = (nextContacts) => {
    const p = nextContacts[0] || {};
    onUpdateBuilder({ contacts: nextContacts, contact: p.name || '', email: p.email || '', phone: p.phone || '' });
  };

  return (
    <div className="h-full flex flex-col" data-testid="builder-detail">
      {/* Header */}
      <div className="px-5 py-3 bg-white border-b border-border flex items-center justify-between shrink-0">
        <div>
          <h3 className="font-semibold text-base">{builder.name}</h3>
          {primaryContact?.name && (
            <p className="text-xs text-muted-foreground mt-0.5">{primaryContact.name}{primaryContact.role ? ` · ${primaryContact.role}` : ''}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={builder.status} type="builder" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onUpdateStatus('tendering')}>Set Tendering</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onUpdateStatus('likely')}>Set Likely</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onMarkWon} className="text-green-600" data-testid="mark-won-menu">
                <Trophy className="w-4 h-4 mr-2" />
                Mark as Winner
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden flex flex-col px-5 pt-3">
        <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 overflow-hidden">
          <TabsList className="w-full justify-start shrink-0 mb-3">
            <TabsTrigger value="contacts">
              Contacts {contacts.length > 0 && <span className="ml-1 text-xs opacity-60">({contacts.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="activities">
              Activities {builder.activities.length > 0 && <span className="ml-1 text-xs opacity-60">({builder.activities.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="overview">Overview</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto pb-4">
            <TabsContent value="contacts" className="mt-0">
              <ContactsTab contacts={contacts} onSync={syncLegacyFields} />
            </TabsContent>

            <TabsContent value="activities" className="mt-0">
              <ActivitiesTab activities={builder.activities} onAddActivity={onAddActivity} />
            </TabsContent>

            <TabsContent value="overview" className="mt-0">
              <OverviewTab builder={builder} onMarkWon={onMarkWon} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

// ─── Contacts Tab ─────────────────────────────────────────────────────────────

function ContactsTab({ contacts, onSync }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', email: '', phone: '' });

  const handleAdd = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Please enter a contact name'); return; }
    const newContact = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: form.name.trim(),
      role: form.role.trim(),
      email: form.email.trim(),
      phone: form.phone.trim()
    };
    onSync([...contacts, newContact]);
    setForm({ name: '', role: '', email: '', phone: '' });
    setShowForm(false);
    toast.success('Contact added');
  };

  const handleRemove = (id) => {
    onSync(contacts.filter(c => c.id !== id));
    toast.success('Contact removed');
  };

  return (
    <div className="space-y-2">
      {contacts.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground py-2">No contacts added yet.</p>
      )}

      {contacts.map(contact => (
        <div key={contact.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-border">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-xs font-semibold text-primary">
              {contact.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{contact.name}</p>
            {contact.role && <p className="text-xs text-muted-foreground">{contact.role}</p>}
            <div className="flex flex-wrap gap-3 mt-1">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                  <Mail className="w-3 h-3" />
                  {contact.email}
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                  <Phone className="w-3 h-3" />
                  {contact.phone}
                </a>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 opacity-0 hover:opacity-100 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
            onClick={() => handleRemove(contact.id)}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}

      {showForm ? (
        <form onSubmit={handleAdd} className="p-3 bg-white rounded-lg border border-border space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} autoFocus />
            <Input placeholder="Role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <Input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm">Add</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => { setShowForm(false); setForm({ name: '', role: '', email: '', phone: '' }); }}>
              <X className="w-3.5 h-3.5 mr-1" />
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="outline" size="sm" className="w-full" onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Add Contact
        </Button>
      )}
    </div>
  );
}

// ─── Activities Tab ───────────────────────────────────────────────────────────

function ActivitiesTab({ activities, onAddActivity }) {
  return (
    <div className="space-y-2">
      <Button variant="outline" size="sm" className="w-full" onClick={onAddActivity} data-testid="add-activity-btn">
        <Plus className="w-4 h-4 mr-1" />
        Log Activity
      </Button>
      {activities.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No activities yet.</p>
      ) : (
        activities.map(activity => <ActivityItem key={activity.id} activity={activity} />)
      )}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ builder, onMarkWon }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground mb-1">Status</p>
          <StatusBadge status={builder.status} type="builder" />
          {builder.status === 'won' && (
            <p className="text-xs text-green-600 mt-2">Winner — quote is an active schedule.</p>
          )}
        </div>
        <div className="p-3 bg-white rounded-lg border border-border">
          <p className="text-xs text-muted-foreground mb-1">Quote</p>
          {builder.quoteId ? (
            <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">Created</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">None yet</span>
          )}
        </div>
      </div>
      {builder.status !== 'won' && (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-green-700 border-green-200 hover:bg-green-50"
          onClick={onMarkWon}
          data-testid="mark-won-btn"
        >
          <Trophy className="w-4 h-4 mr-1.5" />
          Mark as Winner
        </Button>
      )}
    </div>
  );
}

// ─── Activity Item ────────────────────────────────────────────────────────────

function ActivityItem({ activity }) {
  const icons = { note: StickyNote, call: Phone, meeting: Users, email: Mail };
  const Icon = icons[activity.type] || MessageSquare;
  const labels = { note: 'Note', call: 'Call', meeting: 'Meeting', email: 'Email' };

  return (
    <div className="flex gap-3 p-3 bg-white rounded-lg border border-border">
      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{labels[activity.type] || activity.type}</span>
          <span className="text-xs text-muted-foreground">{new Date(activity.createdAt).toLocaleDateString()}</span>
        </div>
        <p className="text-sm">{activity.content}</p>
      </div>
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status, type, small = false }) {
  const statuses = type === 'opportunity' ? OPPORTUNITY_STATUSES : BUILDER_STATUSES;
  const statusConfig = statuses.find(s => s.id === status);

  const colorClasses = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700'
  };

  return (
    <Badge
      variant="secondary"
      className={cn(colorClasses[statusConfig?.color || 'slate'], small && 'text-xs px-1.5 py-0')}
    >
      {statusConfig?.name || status}
    </Badge>
  );
}

// ─── Inline Edit Opportunity Form ─────────────────────────────────────────────

function InlineEditOppForm({ opportunity, onSave, onCancel }) {
  const emptyForm = {
    name: '', developer: '', notes: '', estimatedValue: '',
    industry: '', projectDescription: '', tenderLinkId: '', projectTypes: [],
    status: 'qualification', phase: '',
    jobChaseDate: '', jobChaseNotes: '',
    expectedProjectStart: '', expectedProjectFinish: '', expectedSupplyStart: '',
  };

  const [formData, setFormData] = React.useState(() => ({
    name: opportunity.name || '',
    developer: opportunity.developer || '',
    notes: opportunity.notes || '',
    estimatedValue: opportunity.estimatedValue != null ? String(opportunity.estimatedValue) : '',
    industry: opportunity.industry || '',
    projectDescription: opportunity.projectDescription || '',
    tenderLinkId: opportunity.tenderLinkId || '',
    projectTypes: Array.isArray(opportunity.projectTypes) ? opportunity.projectTypes : [],
    status: opportunity.status || 'qualification',
    phase: opportunity.phase || '',
    jobChaseDate: opportunity.jobChaseDate || '',
    jobChaseNotes: opportunity.jobChaseNotes || '',
    expectedProjectStart: opportunity.expectedProjectStart || '',
    expectedProjectFinish: opportunity.expectedProjectFinish || '',
    expectedSupplyStart: opportunity.expectedSupplyStart || '',
  }));

  // Re-sync if the opportunity changes (e.g. switching between opps while form is open)
  React.useEffect(() => {
    setFormData({
      name: opportunity.name || '',
      developer: opportunity.developer || '',
      notes: opportunity.notes || '',
      estimatedValue: opportunity.estimatedValue != null ? String(opportunity.estimatedValue) : '',
      industry: opportunity.industry || '',
      projectDescription: opportunity.projectDescription || '',
      tenderLinkId: opportunity.tenderLinkId || '',
      projectTypes: Array.isArray(opportunity.projectTypes) ? opportunity.projectTypes : [],
      status: opportunity.status || 'qualification',
      phase: opportunity.phase || '',
      jobChaseDate: opportunity.jobChaseDate || '',
      jobChaseNotes: opportunity.jobChaseNotes || '',
      expectedProjectStart: opportunity.expectedProjectStart || '',
      expectedProjectFinish: opportunity.expectedProjectFinish || '',
      expectedSupplyStart: opportunity.expectedSupplyStart || '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunity.id]);

  const set = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const toggleProjectType = (type) => {
    setFormData(prev => ({
      ...prev,
      projectTypes: prev.projectTypes.includes(type)
        ? prev.projectTypes.filter(t => t !== type)
        : [...prev.projectTypes, type]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Please enter a project name'); return; }
    onSave({
      ...formData,
      estimatedValue: parseFloat(formData.estimatedValue) || 0,
      jobChaseDate: formData.jobChaseDate || null,
      expectedProjectStart: formData.expectedProjectStart || null,
      expectedProjectFinish: formData.expectedProjectFinish || null,
      expectedSupplyStart: formData.expectedSupplyStart || null,
    });
  };

  return (
    <div className="flex-1 overflow-auto bg-muted/10">
      <form onSubmit={handleSubmit} className="h-full flex flex-col">
        {/* Sticky action bar */}
        <div className="sticky top-0 z-10 bg-white border-b border-border px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">Editing Project Details</span>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
            <Button type="submit" size="sm">Save Changes</Button>
          </div>
        </div>

        {/* Form body */}
        <div className="flex-1 p-6">
          <div className="max-w-3xl space-y-6">

            {/* Core fields */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Project Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="ie-name">Project Name *</Label>
                  <Input id="ie-name" value={formData.name} onChange={e => set('name', e.target.value)} placeholder="e.g., Rialto Tower" autoFocus />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ie-developer">Developer</Label>
                  <Input id="ie-developer" value={formData.developer} onChange={e => set('developer', e.target.value)} placeholder="Urban Development Corp" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ie-value">Estimated Value ($)</Label>
                  <Input id="ie-value" type="number" value={formData.estimatedValue} onChange={e => set('estimatedValue', e.target.value)} placeholder="450000" />
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={v => set('status', v)}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {OPPORTUNITY_STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Phase</Label>
                  <Select value={formData.phase} onValueChange={v => set('phase', v)}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {OPPORTUNITY_PHASES.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Industry</Label>
                  <Select value={formData.industry} onValueChange={v => set('industry', v)}>
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_OPTIONS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ie-tenderLinkId">Tender Link ID</Label>
                  <Input id="ie-tenderLinkId" value={formData.tenderLinkId} onChange={e => set('tenderLinkId', e.target.value)} placeholder="TDR-2026-01" />
                </div>
              </div>
            </section>

            {/* Project type */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Project Type</h3>
              <div className="grid grid-cols-3 gap-2 rounded-md border border-border p-3 bg-white">
                {PROJECT_TYPE_OPTIONS.map(type => (
                  <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={formData.projectTypes.includes(type)} onCheckedChange={() => toggleProjectType(type)} />
                    <span className="capitalize">{type}</span>
                  </label>
                ))}
              </div>
            </section>

            {/* Project dates */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Project Dates</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ie-start" className="text-xs text-muted-foreground">Expected Project Start</Label>
                  <Input id="ie-start" type="date" value={formData.expectedProjectStart} onChange={e => set('expectedProjectStart', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ie-finish" className="text-xs text-muted-foreground">Expected Project Finish</Label>
                  <Input id="ie-finish" type="date" value={formData.expectedProjectFinish} onChange={e => set('expectedProjectFinish', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ie-supply" className="text-xs text-muted-foreground">Expected Supply Start</Label>
                  <Input id="ie-supply" type="date" value={formData.expectedSupplyStart} onChange={e => set('expectedSupplyStart', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ie-chase" className="text-xs text-muted-foreground">Job Chase Date</Label>
                  <Input id="ie-chase" type="date" value={formData.jobChaseDate} onChange={e => set('jobChaseDate', e.target.value)} />
                </div>
              </div>
            </section>

            {/* Chase notes + description */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Notes</h3>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ie-jobChaseNotes">Job Chase Notes</Label>
                  <Textarea id="ie-jobChaseNotes" value={formData.jobChaseNotes} onChange={e => set('jobChaseNotes', e.target.value)} placeholder="Follow-up actions, key contacts…" rows={2} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ie-projectDescription">Project Description</Label>
                  <Textarea id="ie-projectDescription" value={formData.projectDescription} onChange={e => set('projectDescription', e.target.value)} placeholder="Describe the project scope…" rows={2} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ie-notes">Additional Notes</Label>
                  <Textarea id="ie-notes" value={formData.notes} onChange={e => set('notes', e.target.value)} placeholder="Additional details…" rows={2} />
                </div>
              </div>
            </section>

            {/* Bottom action bar so user doesn't need to scroll back up */}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

// ─── Dialogs ──────────────────────────────────────────────────────────────────

function NewOpportunityDialog({ open, onClose, onCreate, onUpdate, initial }) {
  const isEdit = !!initial;
  const emptyForm = {
    name: '', developer: '', notes: '', estimatedValue: '',
    industry: '', projectDescription: '', tenderLinkId: '', projectTypes: [],
    status: 'qualification', phase: '',
    jobChaseDate: '', jobChaseNotes: '',
    expectedProjectStart: '', expectedProjectFinish: '', expectedSupplyStart: '',
  };
  const [formData, setFormData] = useState(emptyForm);

  React.useEffect(() => {
    if (open) {
      setFormData(initial
        ? {
            name: initial.name || '',
            developer: initial.developer || '',
            notes: initial.notes || '',
            estimatedValue: initial.estimatedValue != null ? String(initial.estimatedValue) : '',
            industry: initial.industry || '',
            projectDescription: initial.projectDescription || '',
            tenderLinkId: initial.tenderLinkId || '',
            projectTypes: Array.isArray(initial.projectTypes) ? initial.projectTypes : [],
            status: initial.status || 'qualification',
            phase: initial.phase || '',
            jobChaseDate: initial.jobChaseDate || '',
            jobChaseNotes: initial.jobChaseNotes || '',
            expectedProjectStart: initial.expectedProjectStart || '',
            expectedProjectFinish: initial.expectedProjectFinish || '',
            expectedSupplyStart: initial.expectedSupplyStart || '',
          }
        : emptyForm
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  const set = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const toggleProjectType = (type) => {
    setFormData(prev => ({
      ...prev,
      projectTypes: prev.projectTypes.includes(type)
        ? prev.projectTypes.filter(t => t !== type)
        : [...prev.projectTypes, type]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Please enter a project name'); return; }
    const data = {
      ...formData,
      estimatedValue: parseFloat(formData.estimatedValue) || 0,
      jobChaseDate: formData.jobChaseDate || null,
      expectedProjectStart: formData.expectedProjectStart || null,
      expectedProjectFinish: formData.expectedProjectFinish || null,
      expectedSupplyStart: formData.expectedSupplyStart || null,
    };
    if (isEdit) {
      onUpdate(data);
    } else {
      onCreate(data);
      setFormData(emptyForm);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="new-opp-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Opportunity' : 'New Opportunity'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update the opportunity details' : 'Create a new tender opportunity'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Core fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="name">Project Name *</Label>
              <Input id="name" value={formData.name} onChange={e => set('name', e.target.value)} placeholder="e.g., Rialto Tower" data-testid="opp-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="developer">Developer</Label>
              <Input id="developer" value={formData.developer} onChange={e => set('developer', e.target.value)} placeholder="Urban Development Corp" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="value">Estimated Value ($)</Label>
              <Input id="value" type="number" value={formData.estimatedValue} onChange={e => set('estimatedValue', e.target.value)} placeholder="450000" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={v => set('status', v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {OPPORTUNITY_STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Phase</Label>
              <Select value={formData.phase} onValueChange={v => set('phase', v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {OPPORTUNITY_PHASES.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Industry</Label>
              <Select value={formData.industry} onValueChange={v => set('industry', v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {INDUSTRY_OPTIONS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tenderLinkId">Tender Link ID</Label>
              <Input id="tenderLinkId" value={formData.tenderLinkId} onChange={e => set('tenderLinkId', e.target.value)} placeholder="TDR-2026-01" />
            </div>
          </div>

          {/* Project type */}
          <div className="space-y-1.5">
            <Label>Project Type</Label>
            <div className="grid grid-cols-2 gap-2 rounded-md border border-border p-3">
              {PROJECT_TYPE_OPTIONS.map(type => (
                <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={formData.projectTypes.includes(type)} onCheckedChange={() => toggleProjectType(type)} />
                  <span className="capitalize">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Project Dates</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="expectedProjectStart" className="text-xs text-muted-foreground">Expected Project Start</Label>
                <Input id="expectedProjectStart" type="date" value={formData.expectedProjectStart} onChange={e => set('expectedProjectStart', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="expectedProjectFinish" className="text-xs text-muted-foreground">Expected Project Finish</Label>
                <Input id="expectedProjectFinish" type="date" value={formData.expectedProjectFinish} onChange={e => set('expectedProjectFinish', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="expectedSupplyStart" className="text-xs text-muted-foreground">Expected Supply Start</Label>
                <Input id="expectedSupplyStart" type="date" value={formData.expectedSupplyStart} onChange={e => set('expectedSupplyStart', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="jobChaseDate" className="text-xs text-muted-foreground">Job Chase Date</Label>
                <Input id="jobChaseDate" type="date" value={formData.jobChaseDate} onChange={e => set('jobChaseDate', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Job chase notes */}
          <div className="space-y-1.5">
            <Label htmlFor="jobChaseNotes">Job Chase Notes</Label>
            <Textarea id="jobChaseNotes" value={formData.jobChaseNotes} onChange={e => set('jobChaseNotes', e.target.value)} placeholder="Follow-up actions, key contacts…" rows={2} />
          </div>

          {/* Description / notes */}
          <div className="space-y-1.5">
            <Label htmlFor="projectDescription">Project Description</Label>
            <Textarea id="projectDescription" value={formData.projectDescription} onChange={e => set('projectDescription', e.target.value)} placeholder="Describe the project scope…" rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" value={formData.notes} onChange={e => set('notes', e.target.value)} placeholder="Additional details…" rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="create-opp-submit">{isEdit ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewBuilderDialog({ open, onClose, onCreate }) {
  const [formData, setFormData] = useState({ name: '', contact: '', email: '', phone: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Please enter a builder name'); return; }
    const primaryContact = { name: formData.contact.trim(), email: formData.email.trim(), phone: formData.phone.trim(), role: 'Primary Contact' };
    onCreate({
      ...formData,
      contacts: primaryContact.name || primaryContact.email || primaryContact.phone ? [primaryContact] : []
    });
    setFormData({ name: '', contact: '', email: '', phone: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-builder-dialog">
        <DialogHeader>
          <DialogTitle>Add Builder</DialogTitle>
          <DialogDescription>Add a builder to this opportunity</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="builderName">Builder Name *</Label>
            <Input id="builderName" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Brookfield Construction" data-testid="builder-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact">Primary Contact</Label>
            <Input id="contact" value={formData.contact} onChange={e => setFormData({ ...formData, contact: e.target.value })} placeholder="e.g., John Smith" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="add-builder-submit">Add Builder</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddActivityDialog({ open, onClose, onAdd }) {
  const [formData, setFormData] = useState({ type: 'note', content: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.content.trim()) { toast.error('Please enter activity details'); return; }
    onAdd(formData);
    setFormData({ type: 'note', content: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="add-activity-dialog">
        <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={formData.type} onValueChange={v => setFormData({ ...formData, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="content">Details</Label>
            <Textarea id="content" value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })} placeholder="Activity details…" rows={3} data-testid="activity-content-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="add-activity-submit">Add</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
